import PdfPreview from "../ui/PdfPreview"
import { useId } from "react"
import { SpinnerText } from "../ui/Spinner"
import { FiFileText, FiUpload } from "react-icons/fi"
import { handleFileDrop } from "../../utils/fileUploadUtils"

// Shared upload drop-zone used by the EP1001 patent documents form
// (DocumentsSection) and the EP1200 entry-into-European-phase form
// (PatentEpPhaseFormPage) — same drag/drop area and same "Replace"/"Delete"
// labels, with accepted file type configured by the caller.
export default function FileUploadSlot({
  label,
  fileName,
  documentId,
  uploading,
  error,
  disabled = false,
  accept = "application/pdf",
  helperText = "PDF, max. 20 MB",
  onSelect,
  onDelete
}: {
  label?: string
  fileName?: string | null
  documentId?: string | null
  uploading: boolean
  error?: string
  disabled?: boolean
  accept?: string
  helperText?: string
  onSelect: (file: File) => void
  onDelete: () => void
}) {
  const inputId = useId()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && !disabled) onSelect(file)
    event.target.value = ""
  }

  return (
    <div>
      {label && (
        <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      )}

      <div
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => !disabled && !fileName && handleFileDrop(event, onSelect)}
        className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors ${
          fileName
            ? "border-gray-200 bg-gray-50"
            : "border-dashed border-gray-300 hover:border-orange-400"
        } ${disabled ? "opacity-50" : ""}`}
      >
        {fileName ? (
          <>
            <div className="flex min-w-0 items-center gap-2 text-gray-700">
              <FiFileText size={16} className="shrink-0 text-gray-400" />
              {documentId && fileName.toLowerCase().endsWith(".pdf")
                ? <PdfPreview documentId={documentId} provider="patent" fileName={fileName} />
                : <span className="truncate">{fileName}</span>}
            </div>

            {!disabled && (
              <div className="flex shrink-0 items-center gap-3">
                <label
                  htmlFor={inputId}
                  className="cursor-pointer text-xs font-medium text-dantzBlue hover:underline"
                >
                  {uploading ? <SpinnerText>Uploading…</SpinnerText> : "Replace"}
                </label>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={uploading}
                  className="text-xs font-medium text-red-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            )}
          </>
        ) : (
          <label
            htmlFor={inputId}
            className={`flex w-full items-center gap-2 ${disabled ? "cursor-not-allowed" : "cursor-pointer"} text-gray-500`}
          >
            <FiUpload size={16} className="shrink-0" />
            <span>{uploading ? <SpinnerText>Uploading…</SpinnerText> : "Click to upload or drag & drop"}</span>
            <span className="ml-auto shrink-0 text-xs text-gray-400">{helperText}</span>
          </label>
        )}

        <input
          id={inputId}
          type="file"
          accept={accept}
          disabled={disabled || uploading}
          onChange={handleChange}
          className="hidden"
        />
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
